import { useTranslation } from 'react-i18next';
import { Eye, FileText, Trash2, AlertTriangle } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { formatShortDate } from '../../utils/dateUtils';
import type { ParsedPolicy } from '../department-policies/upload/DocxPolicyUpload';

interface FacilityPolicyListProps {
    policies: ParsedPolicy[];
    onRemove: (policy: ParsedPolicy) => Promise<void>;
    onPreview: (policy: ParsedPolicy) => void;
}

export default function FacilityPolicyList({
    policies,
    onRemove,
    onPreview,
}: FacilityPolicyListProps) {
    const { toast } = useToast();
    const { t } = useTranslation();

    const handleRemove = async (policy: ParsedPolicy) => {
        try {
            await onRemove(policy);
        } catch {
            toast({
                title: t('facilities.policyList.removeFailed'),
                description: t('facilities.policyList.removeFailedDesc', { title: policy.title }),
                variant: 'destructive',
            });
        }
    };

    if (policies.length === 0) return null;

    return (
        <div className="space-y-1.5">
            {policies.map((policy) => {
                const key = policy._id ?? policy.title;
                return (
                    <div
                        key={key}
                        className="flex items-center gap-3 rounded-lg border border-subtle bg-muted px-4 py-2.5"
                    >
                        <FileText
                            className="h-4 w-4 shrink-0 text-muted-foreground"
                            aria-hidden="true"
                        />
                        <div className="min-w-0 flex-1 space-y-0.5">
                            <p className="text-sm font-medium text-foreground">
                                {policy.title}
                            </p>
                            <div className="flex flex-wrap items-center gap-1.5">
                                <span className="text-xs text-muted-foreground">
                                    {t('facilities.policyList.effective', { date: formatShortDate(policy.effectiveDate) })}
                                </span>
                                {policy.tags.map((tag) => (
                                    <Badge
                                        key={tag}
                                        variant="outline"
                                        className="border-transparent bg-gray-200 text-xs text-gray-800"
                                    >
                                        {tag}
                                    </Badge>
                                ))}
                                {policy.warnings.length > 0 && (
                                    <span className="flex items-center gap-1 text-xs text-amber-400">
                                        <AlertTriangle
                                            className="h-3 w-3"
                                            aria-hidden="true"
                                        />
                                        {t('facilities.policyList.warningCount', { count: policy.warnings.length })}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onPreview(policy)}
                                aria-label={`Preview ${policy.title}`}
                            >
                                <Eye
                                    className="h-3.5 w-3.5"
                                    aria-hidden="true"
                                />
                                <span className="ml-1.5 hidden sm:inline">
                                    {t('facilities.policyList.preview')}
                                </span>
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                onClick={() => handleRemove(policy)}
                                aria-label={`Remove ${policy.title}`}
                            >
                                <Trash2
                                    className="h-3.5 w-3.5"
                                    aria-hidden="true"
                                />
                            </Button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
